import { useEffect, useRef, useCallback } from "react";
import { INotification } from "../types/INotification";

interface UseInsightNotificationWatcherParams {
  notifications: INotification[];
  onInsightNotificationReceived: () => void;
}

const MAX_PROCESSED_IDS = 100;
const DEBOUNCE_DELAY = 1000;

/**
 * Hook that watches Firebase notifications and triggers callback when
 * detecting notifications with "insight" in the title (case-insensitive)
 **/
export function useInsightNotificationWatcher({
  notifications,
  onInsightNotificationReceived,
}: UseInsightNotificationWatcherParams) {
  // Maintains Set of already processed IDs (more efficient than array)
  const processedIdsRef = useRef<Set<string>>(new Set());
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingInsightFoundRef = useRef<boolean>(false);

  // Stable callback using useCallback
  const stableCallback = useCallback(() => {
    onInsightNotificationReceived();
  }, [onInsightNotificationReceived]);

  useEffect(() => {
    // Initialize with existing IDs on first mount
    if (processedIdsRef.current.size === 0 && notifications.length > 0) {
      notifications.forEach((notification) => {
        processedIdsRef.current.add(notification.id);
      });
      return;
    }

    // Identify new notifications (not yet processed)
    const newNotifications = notifications.filter(
      (notification) => !processedIdsRef.current.has(notification.id)
    );

    // If there are no new notifications, do nothing
    if (newNotifications.length === 0) {
      return;
    }

    // Add new IDs to the processed Set
    newNotifications.forEach((notification) => {
      processedIdsRef.current.add(notification.id);
    });

    // Limit Set size (keep only last MAX_PROCESSED_IDS)
    if (processedIdsRef.current.size > MAX_PROCESSED_IDS) {
      const idsArray = Array.from(processedIdsRef.current);
      const idsToKeep = idsArray.slice(-MAX_PROCESSED_IDS);
      processedIdsRef.current = new Set(idsToKeep);
    }

    // Check if any new notification contains "insight" in the title
    const hasInsightNotification = newNotifications.some((notification) =>
      notification.title.toLowerCase().includes("insight")
    );

    if (hasInsightNotification) {
      pendingInsightFoundRef.current = true;

      // Cancel previous timer if it exists
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Schedule callback execution with debounce
      debounceTimerRef.current = setTimeout(() => {
        if (pendingInsightFoundRef.current) {
          stableCallback();
          pendingInsightFoundRef.current = false;
        }
        debounceTimerRef.current = null;
      }, DEBOUNCE_DELAY);
    }

    // Cleanup timer when component unmounts or dependencies change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [notifications, stableCallback]);
}
